import React, { useState, useRef } from "react";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import styled from "styled-components";
import CancelIcon from "@coworker/components/src/assets/svg/icon-close-large.svg?react";
import { addArticlesBulk } from "../RoomSettings/services/articles.service";
import statusTypes from "@coworker/enums/statusTypes";
import { Article } from "../RoomSettings/types/article";
import { useQueryClient } from "@tanstack/react-query";
import { useInputPopup } from "./index";
import { SimpleToast } from "@coworker/apprestructured/src/shared/simple/SimpleToast/SimpleToast";
import { Trans, useTranslation } from "@coworker/locales";
import { correctArticleNumberLength } from "../../helpers/correctArticleNumberLength";
import { fetchItemsInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { QueryKeys } from "../RoomSettings/hooks/queryKeys";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { ProductSearchComponent } from "@coworker/apprestructured/src/scan-and-search/pages/product-search-page/ProductSearchComponent";
import { fetchItemsInfoFromDataService } from "../RoomSettings/services/localCombination.service";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  transformArticlesInfos,
  ProductArticleIdAndQuantity,
} from "./AddMultipleArticles.helper";
import {
  detectBarcodeFormat,
  shouldRestartScan,
} from "@coworker/apprestructured/src/scan-and-search/pages/hdi-scanner-page/hdiScannerPageUtils";
import { extractBasedOnSymbology } from "@coworker/apprestructured/src/scan-and-search/helpers/extractBasedOnSymbologyUtils";
import { SymbologyType } from "@coworker/apprestructured/src/scan-and-search/types/SymbologyType";
import { SearchItemCardRenderType } from "@coworker/apprestructured/src/scan-and-search/types/SearchItemCardRenderType";

const MediaSettingsPopupContainer = styled("div")`
  background: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  overflow: auto;
`;
const MediaSettingsPopupTitle = styled("h1")`
  display: flex;
  justify-content: flex-end;
`;
const HeadingWrapper = styled("h1")`
  font-size: 16px;
  font-weight: bold;
  color: var(--grey900);
  padding: 25px;
  margin: 0 auto;
`;
const HeadingAndButtonWrapper = styled("div")`
  display: inline-flex;
`;
const PaddedHorizontalContainer = styled("div")`
  width: 312px;
  padding: 0 24px;
  z-index: 1;
`;

const ModalItemWrapper = styled("div")`
  > * + * {
    margin-top: 20px;
  }

  margin-bottom: 90px;
`;
const CancelButton = styled("button")`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: relative;
  margin-right: 7px;
  margin-left: 23px;
  margin-top: 15px;
  background: none;
`;

const CloseIcon = styled(CancelIcon)`
  color: var(--grey900);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const InformationBox = styled("div")`
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: normal;
  color: var(--grey700);
  padding: 13px 2px 2px 9px;
`;

const InformationBoxFirstLine = styled("div")`
  font-size: 14px;
  letter-spacing: normal;
  color: var(--grey700);
  line-height: 1.5;
  margin-left: 15px;
`;

const InformationBoxSecondLine = styled("div")`
  font-size: 14px;
  letter-spacing: normal;
  color: var(--grey700);
  margin-left: 15px;
  margin-bottom: 7px;
`;

const Seperator = styled("div")`
  border-top: 2px solid var(--grey150);
  width: 140px;
`;

const SeparatorWrapper = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ButtonWrapperFixed = styled("div")`
  position: fixed;
  display: flex;
  bottom: 0;
  width: 100%;
  background: white;
  border-top: 1px solid var(--grey150);
  align-items: center;
  z-index: 2;
`;

const ButtonWrapperInner = styled("div")`
  padding: 24px;
  width: 100%;
`;

const ResultWrapText = styled("div")`
  font-size: 12px;
  padding: 0 10px;
`;

interface AddMultipleArticlesMobileType {
  isOpen: boolean;
  mediaId: string;
  groupId: string;
  countryId: string;
  setIsAddingArticles: React.Dispatch<React.SetStateAction<boolean>>;
}

export function AddMultipleArticlesMobile(
  props: AddMultipleArticlesMobileType
) {
  const [inputValue, setInputValue] = useState<string>("");
  const queryClient = useQueryClient();
  const [toastVisible, setToastVisible] = useState(false);
  const [toastText, setToastText] = useState("");
  const { t } = useTranslation();
  const { closePopup } = useInputPopup();
  const storeId = useStoreId();
  const userCountryId = useCountryId();

  const scanBufferRef = useRef<string>("");
  const lastKeyTimeRef = useRef<number>(0);
  const lastTimeoutRef = useRef<NodeJS.Timeout>(null);
  const SCAN_TIMEOUT = 100;

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const handleLines = (lines: string[]) => {
      let symbology: SymbologyType = "INCOMPLETE";
      const cleanedLines = lines
        .filter((line) => line.trim() !== "")
        .map((line) => {
          symbology = detectBarcodeFormat(line);
          const articleInfo = extractBasedOnSymbology(symbology, line);
          return articleInfo.articleNumber;
        });

      let cleanedInput = `${cleanedLines.join("\n")}`;
      if (symbology !== "INCOMPLETE" && symbology !== "UNKNOWN") {
        cleanedInput += "\n";
      }
      setInputValue(cleanedInput);

      scanBufferRef.current = "";
      lastKeyTimeRef.current = 0;
    };

    const lines = e.target.value.split("\n");
    const lastLine = lines[lines.length - 1];
    const currentTime = Date.now();
    if (lastKeyTimeRef.current === 0) {
      lastKeyTimeRef.current = currentTime;
    }

    if (!shouldRestartScan(lastKeyTimeRef.current, SCAN_TIMEOUT)) {
      if (lastTimeoutRef?.current) {
        clearTimeout(lastTimeoutRef.current);
      }
      scanBufferRef.current += lastLine;
      lastKeyTimeRef.current = currentTime;

      // Check after a delay if the scan has ended. This is needed as, once the scan character
      // stream has ended there is no further event to get 'handleLines' to be executed.
      // Hence the need for a timeout.
      // Note! Several timers will be started. But only the last one will trigger 'handleLines' as
      // lastKeyTimeRef will always be less than 'SCAN_TIMEOUT' while the scan characters keeps coming
      // (except when the scan has ended)
      lastTimeoutRef.current = setTimeout(() => {
        if (shouldRestartScan(lastKeyTimeRef.current, SCAN_TIMEOUT)) {
          if (scanBufferRef.current) {
            lines[lines.length - 1] = scanBufferRef.current;
            handleLines(lines);
          }
        }
      }, SCAN_TIMEOUT * 2); // Make sure that shouldRestartScan returns true
      return;
    }

    handleLines(lines);
  };

  const handleSubmit = async () => {
    if (props.setIsAddingArticles) props.setIsAddingArticles(true);
    closePopup();
    const articles =
      typeof inputValue === "string" ? inputValue.split("\n") : inputValue;
    const articleCountMap: { [id: string]: number } = {};
    const articleIdsWithQuantity: { articleId: string; quantity: number }[] =
      [];
    articles.forEach((article) => {
      const [id, quantity] = article.split(" ");
      if (id && articleCountMap[id]) {
        articleCountMap[id] += quantity ? parseInt(quantity, 10) : 1;
      } else if (id) {
        articleCountMap[id] = quantity ? parseInt(quantity, 10) : 1;
      }
    });

    Object.keys(articleCountMap).forEach((key) => {
      if (key !== "") {
        const quantity = articleCountMap[key];
        if (quantity !== undefined) {
          articleIdsWithQuantity.push({
            articleId: key,
            quantity,
          });
        }
      }
    });

    if (articleIdsWithQuantity.length > 0) {
      const articleIds = articleIdsWithQuantity.map(
        (article: { articleId: string; quantity: number }) =>
          correctArticleNumberLength(article.articleId.trim())
      );

      const itemNos = articleIds.join(",");
      const articleInfoList: Item[] = await queryClient.ensureQueryData({
        queryKey: ["items", "info", itemNos],
        queryFn: () => fetchItemsInfo(itemNos),
      });

      // Filter out articles which do not have article data to add them as unknown articles
      const articleInfosNotAdded = articleIds
        .filter(
          (articleId) =>
            !articleInfoList
              .map((articleData) => articleData.no)
              .filter((id) => id)
              .includes(articleId)
        )
        .map((articleId) => {
          return { no: articleId } as Item;
        });
      // Filter articles without article data and fetch their info from data-service
      const formattedInputValues: ProductArticleIdAndQuantity[] = [];
      articleIdsWithQuantity.forEach(({ articleId, quantity }) => {
        formattedInputValues.push({
          productArticleId: articleId,
          quantity,
        });
      });

      if (articleInfosNotAdded.length > 0) {
        const articlesWithoutDataNos = articleInfosNotAdded
          .map((article) => article.no)
          .join(",");

        const fetchedCombinations =
          (await fetchItemsInfoFromDataService(
            storeId,
            userCountryId,
            articlesWithoutDataNos
          )) ?? [];

        // Collect all combination item numbers
        const combinationEntries = fetchedCombinations.map((combo) => ({
          parentArticle: combo.no,
          itemNos: combo?.itemsInCombination?.map((i) => i.item_no),
        }));

        // Update formattedInputValues and collect itemNos
        const allCombinationItemNos = combinationEntries.flatMap(
          ({ parentArticle, itemNos }) => {
            itemNos?.forEach((no, index) => {
              const quantity =
                fetchedCombinations.find((combo) => combo.no === parentArticle)
                  ?.itemsInCombination?.[index]?.quantity || 1; // Default to 1 if quantity is not found

              formattedInputValues.push({
                productArticleId: no,
                quantity,
                parentArticle,
                articlesCountInCombination: quantity,
              });
            });
            return itemNos;
          }
        );

        // Fetch detailed info for all items in combinations
        const articlesInCombinationInfo: Item[] =
          await queryClient.ensureQueryData({
            queryKey: ["items", "info", allCombinationItemNos],
            queryFn: () => fetchItemsInfo(allCombinationItemNos.join(",")),
          });

        // Merge articles with and without data (fallback lookup in data service)
        articleInfoList.push(
          ...articlesInCombinationInfo,
          ...fetchedCombinations
        );
      }

      const allArticles = transformArticlesInfos(
        props.mediaId,
        formattedInputValues,
        articleInfoList
      );

      if (articleInfoList.length > 0) {
        try {
          await addArticlesBulk(
            allArticles.map((articleData) => {
              const article = articleIdsWithQuantity.find(
                (articleIdAndQuantity) =>
                  articleIdAndQuantity.articleId ===
                  articleData.productArticleId
              );

              return {
                ...articleData,
                active: statusTypes.ACTIVE,
                nbrArticles:
                  article && article.quantity
                    ? article.quantity
                    : articleData.nbrArticles,
                mediaId: props.mediaId,
              } as Article;
            }),
            props.groupId
          );
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.ArticlesByMedia],
            }),
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.ArticlesByGroup],
            }),
          ]);
        } catch {
          alert(t("searchErrorString"));
        }
      } else {
        setToastText(t("noDataMessage"));
        setToastVisible(true);
      }
    }
    if (props.setIsAddingArticles) props.setIsAddingArticles(false);
  };

  const onClose = () => {
    closePopup();
  };
  const [searchQuery, setSearchQuery] = useState("");

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const key = event.key;
    // Check if Ctrl or Cmd is pressed along with 'v'
    const isPaste =
      (event.ctrlKey || event.metaKey) && key.toLowerCase() === "v";
    const isAllowedKey = [
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "Enter",
      "Backspace",
    ].includes(key);
    // Leaving the Enter and Backspace keys to work as default and characters from a to z except 'v' to be prevented since we want to allow paste.
    if (key.match(/[^0-9/]/) && !isPaste && !isAllowedKey) {
      // Prevent default action if the key is anything but a number
      event.preventDefault();
    }
  }

  const handleProductClick = (product: Item) => {
    setInputValue((prev) =>
      prev ? `${prev}\n${product.no}` : `${product.no}`
    );
  };

  return (
    <>
      <MediaSettingsPopupContainer>
        <HeadingAndButtonWrapper>
          <HeadingWrapper>
            <Trans>addArticlesString</Trans>
          </HeadingWrapper>
          <MediaSettingsPopupTitle>
            <CancelButton
              onClick={onClose}
              data-testid="closeAddmultipleArticles"
            >
              <CloseIcon />
            </CancelButton>
          </MediaSettingsPopupTitle>
        </HeadingAndButtonWrapper>
        <ProductSearchComponent
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          itemCardRenderType={SearchItemCardRenderType.button}
          handleItemCardClick={handleProductClick}
          shouldHideSPRItems={false}
          shouldHideLocalCombinationItems={false}
        />
        <PaddedHorizontalContainer>
          <SeparatorWrapper>
            <Seperator />
            <ResultWrapText>Or</ResultWrapText>
            <Seperator />
          </SeparatorWrapper>
          <InformationBox>
            <Trans>scanArticleRDTString</Trans>
          </InformationBox>
          <InformationBoxFirstLine>
            {"\u2022"} <Trans>oneArticlePerLineString</Trans>
          </InformationBoxFirstLine>
          <InformationBoxSecondLine>
            {"\u2022"} <Trans>scanMultipleTimesToIncreaseQuantityString</Trans>
          </InformationBoxSecondLine>
          <ModalItemWrapper>
            <FixaTextArea
              autoFocus={true}
              label={""}
              value={inputValue}
              style={{ height: "200px" }}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onClick={(event) => {
                event.stopPropagation();
              }}
            />
          </ModalItemWrapper>
        </PaddedHorizontalContainer>
        <ButtonWrapperFixed>
          <ButtonWrapperInner>
            <FixaButton
              type="primary"
              key="addMultipleArticles"
              text={t("addArticlesString")}
              fluid={true}
              onClick={handleSubmit}
              iconPosition="trailing"
            />
          </ButtonWrapperInner>
        </ButtonWrapperFixed>
        {toastVisible && (
          <SimpleToast
            text={toastText}
            isOpen={toastVisible}
            onCloseRequest={() => {
              setToastVisible(false);
            }}
            ariaLabel={t("noDataMessage")}
          />
        )}
      </MediaSettingsPopupContainer>
    </>
  );
}
